import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import ErrorBanner from '../components/ErrorBanner';
import type { AppDetail } from '../types';
import { reportErrorLocal } from '../utils/errors';
import './DebuggingPage.lane44.css';

interface StepTiming {
  duration_ms?: number;
  error?: string;
}

interface ToolFailureOutput {
  command?: string;
  exit_code?: number | null;
  stdout?: string;
  stderr?: string;
}

interface DebugInfo {
  container: string;
  timings?: Record<string, StepTiming>;
  processes?: {
    raw: string;
    processes: Array<{
      pid: string;
      ppid: string;
      user: string;
      stat: string;
      comm: string;
      args: string;
    }>;
  };
  processes_error?: string;
  processes_failure_output?: ToolFailureOutput;
  gdb_sidecar?: {
    method: string;
    traces: Array<{
      pid: string;
      comm: string;
      backtrace: string;
    }>;
    errors: string[];
  };
  gdb_sidecar_error?: string;
  gdb_sidecar_failure_output?: ToolFailureOutput;
  rust_gdb?: {
    method: string;
    available: boolean;
    reason?: string;
    raw_ps_output?: string;
    raw_probe_output?: string;
    traces?: Array<{
      pid: string;
      comm: string;
      backtrace: string;
    }>;
    errors?: string[];
  };
  rust_gdb_error?: string;
  rust_gdb_failure_output?: ToolFailureOutput;
  proc_stacks?: {
    method: string;
    available?: boolean;
    reason?: string;
    raw_ps_output?: string;
    stacks?: Array<{
      pid: string;
      comm: string;
      proc_info: string;
    }>;
    errors?: string[];
  };
  proc_stacks_error?: string;
  proc_stacks_failure_output?: ToolFailureOutput;
  async_taskdump?: {
    taskdump: string;
    tokio_console_enabled: boolean;
  };
}

interface DebugProgress {
  current_step: string;
  started_at?: string;
  total_duration_ms?: number;
  timings: Record<string, StepTiming>;
}

export default function DebuggingPage({
  apiClient,
  setError
}: {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
}) {
  const params = useParams();
  const navigate = useNavigate();
  const appId = params.appId ?? '';
  const [detail, setDetail] = useState<AppDetail | null>(null);

  // Logs state
  const [output, setOutput] = useState('');
  const [container, setContainer] = useState<string>('');
  const [lines, setLines] = useState<number>(200);
  const [logsError, setLogsError] = useState<string | null>(null);

  // Debug info state
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);
  const [debugInfoLoading, setDebugInfoLoading] = useState(false);
  const [debugInfoError, setDebugInfoError] = useState<string | null>(null);
  const [debugInfoCopied, setDebugInfoCopied] = useState(false);
  const [debugProgress, setDebugProgress] = useState<DebugProgress | null>(null);

  useEffect(() => {
    if (!appId) {
      return;
    }
    apiClient
      .get<AppDetail>(`/api/apps/${appId}`)
      .then((data) => {
        setDetail(data);
        setContainer((current) => current || data.app.container_names[0] || '');
      })
      .catch((err) => reportErrorLocal('Failed to load app', err, setError, setLogsError));
  }, [apiClient, appId, setError]);

  async function fetchLogs() {
    if (!appId) {
      return;
    }
    const query = new URLSearchParams();
    if (container) {
      query.set('container', container);
    }
    query.set('lines', String(lines));
    try {
      const data = await apiClient.get<{ output: string }>(
        `/api/apps/${appId}/logs?${query.toString()}`
      );
      setOutput(data.output);
    } catch (err) {
      reportErrorLocal('Failed to load logs', err, setError, setLogsError);
    }
  }

  const fetchDebugInfo = async () => {
    if (!appId) return;
    setDebugInfoLoading(true);
    setDebugInfoError(null);
    setDebugInfo(null);
    setDebugProgress(null);

    try {
      // Start debug collection
      const query = new URLSearchParams();
      if (container) {
        query.set('container', container);
      }

      const startResponse = await apiClient.post<{ job_id: string; status: string }>(
        `/api/apps/${appId}/debug-info?${query.toString()}`,
        {}
      );

      const jobId = startResponse.job_id;

      // Poll for result
      let attempts = 0;
      const maxAttempts = 60; // 60 * 2s = 2 minutes max
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2s between polls

        const jobResponse = await apiClient.get<{
          status: string;
          container: string;
          progress?: DebugProgress;
          result?: DebugInfo;
          error?: string;
        }>(`/api/apps/${appId}/debug-info/${jobId}`);

        // Update progress display
        if (jobResponse.progress) {
          setDebugProgress(jobResponse.progress);
        }

        if (jobResponse.status === 'complete' && jobResponse.result) {
          setDebugInfo(jobResponse.result);
          setDebugInfoLoading(false);
          setDebugProgress(null);
          return;
        } else if (jobResponse.status === 'failed') {
          throw new Error(jobResponse.error || 'Debug collection failed');
        }

        attempts++;
      }

      throw new Error('Debug collection timed out');
    } catch (err) {
      setDebugInfoError(err instanceof Error ? err.message : 'Failed to fetch debug info');
      setDebugInfoLoading(false);
      setDebugProgress(null);
    }
  };

  const copyDebugInfo = async () => {
    if (!debugInfo) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2));
      setDebugInfoCopied(true);
      setTimeout(() => setDebugInfoCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy debug info:', err);
    }
  };

  const formatDebugInfoAsText = (info: DebugInfo): string => {
    const lines: string[] = [];
    const appendFailureOutput = (
      section: string,
      failure?: ToolFailureOutput
    ) => {
      if (!failure) {
        return;
      }
      lines.push(`--- ${section} FAILURE OUTPUT ---`);
      if (failure.command) {
        lines.push(`Command: ${failure.command}`);
      }
      if (failure.exit_code !== undefined && failure.exit_code !== null) {
        lines.push(`Exit code: ${failure.exit_code}`);
      }
      if (failure.stdout) {
        lines.push('STDOUT:');
        lines.push(failure.stdout);
      }
      if (failure.stderr) {
        lines.push('STDERR:');
        lines.push(failure.stderr);
      }
      lines.push('');
    };
    lines.push('=== CONTAINER DEBUG INFO ===');
    lines.push(`Generated at: ${new Date().toISOString()}`);
    lines.push(`Container: ${info.container}`);
    lines.push('');

    // Timings summary
    if (info.timings) {
      lines.push('--- TIMINGS ---');
      const entries = Object.entries(info.timings);
      if (entries.length > 0) {
        let total = 0;
        for (const [step, timing] of entries) {
          const duration = timing.duration_ms ?? 0;
          total += duration;
          const status = timing.error ? ' (error)' : '';
          lines.push(`  ${step}: ${duration}ms${status}`);
        }
        lines.push(`  TOTAL: ${total}ms`);
      }
      lines.push('');
    }

    // Processes
    if (info.processes_error) {
      lines.push('--- PROCESSES ERROR ---');
      lines.push(info.processes_error);
      lines.push('');
      appendFailureOutput('PROCESSES', info.processes_failure_output);
    } else if (info.processes) {
      lines.push('--- PROCESSES ---');
      for (const p of info.processes.processes) {
        lines.push(`[${p.pid}] ${p.comm} (ppid: ${p.ppid}, user: ${p.user}, stat: ${p.stat})`);
        if (p.args) {
          lines.push(`  ${p.args}`);
        }
      }
      lines.push('');
    }

    // GDB Sidecar backtraces
    if (info.gdb_sidecar_error) {
      lines.push('--- GDB SIDECAR ERROR ---');
      lines.push(info.gdb_sidecar_error);
      lines.push('');
      appendFailureOutput('GDB SIDECAR', info.gdb_sidecar_failure_output);
    } else if (info.gdb_sidecar) {
      lines.push('--- GDB SIDECAR BACKTRACES ---');
      lines.push(`Method: ${info.gdb_sidecar.method}`);
      if (info.gdb_sidecar.errors?.length) {
        lines.push(`Errors: ${info.gdb_sidecar.errors.join(', ')}`);
      }
      lines.push('');
      for (const bt of info.gdb_sidecar.traces) {
        lines.push(`[${bt.pid}] ${bt.comm}`);
        if (bt.backtrace) {
          for (const traceLine of bt.backtrace.split('\n').slice(0, 50)) {
            lines.push(`  ${traceLine}`);
          }
          if (bt.backtrace.split('\n').length > 50) {
            lines.push('  ... (truncated)');
          }
        }
        lines.push('');
      }
    }

    // rust-gdb backtraces
    if (info.rust_gdb_error) {
      lines.push('--- RUST-GDB ERROR ---');
      lines.push(info.rust_gdb_error);
      lines.push('');
      appendFailureOutput('RUST-GDB', info.rust_gdb_failure_output);
    } else if (info.rust_gdb) {
      lines.push('--- RUST-GDB BACKTRACES ---');
      lines.push(`Available: ${info.rust_gdb.available}`);
      if (info.rust_gdb.reason) {
        lines.push(`Reason: ${info.rust_gdb.reason}`);
      }
      if (info.rust_gdb.raw_ps_output) {
        lines.push('Process probe output:');
        lines.push(info.rust_gdb.raw_ps_output);
      }
      if (info.rust_gdb.raw_probe_output) {
        lines.push('Tool probe output:');
        lines.push(info.rust_gdb.raw_probe_output);
      }
      if (info.rust_gdb.errors?.length) {
        lines.push(`Errors: ${info.rust_gdb.errors.join(', ')}`);
      }
      if (info.rust_gdb.traces?.length) {
        lines.push('');
        for (const bt of info.rust_gdb.traces) {
          lines.push(`[${bt.pid}] ${bt.comm}`);
          if (bt.backtrace) {
            for (const traceLine of bt.backtrace.split('\n').slice(0, 50)) {
              lines.push(`  ${traceLine}`);
            }
            if (bt.backtrace.split('\n').length > 50) {
              lines.push('  ... (truncated)');
            }
          }
          lines.push('');
        }
      }
      lines.push('');
    }

    // /proc stacks
    if (info.proc_stacks_error) {
      lines.push('--- PROC STACKS ERROR ---');
      lines.push(info.proc_stacks_error);
      lines.push('');
      appendFailureOutput('PROC STACKS', info.proc_stacks_failure_output);
    } else if (info.proc_stacks) {
      lines.push('--- /PROC STACKS ---');
      lines.push(`Method: ${info.proc_stacks.method}`);
      if (info.proc_stacks.available === false) {
        lines.push(`Available: false`);
        if (info.proc_stacks.reason) {
          lines.push(`Reason: ${info.proc_stacks.reason}`);
        }
      } else {
        if (info.proc_stacks.raw_ps_output) {
          lines.push('Process probe output:');
          lines.push(info.proc_stacks.raw_ps_output);
        }
        if (info.proc_stacks.errors?.length) {
          lines.push(`Errors: ${info.proc_stacks.errors.join(', ')}`);
        }
        if (info.proc_stacks.stacks?.length) {
          lines.push('');
          for (const s of info.proc_stacks.stacks) {
            lines.push(`[${s.pid}] ${s.comm}`);
            if (s.proc_info) {
              for (const infoLine of s.proc_info.split('\n').slice(0, 20)) {
                lines.push(`  ${infoLine}`);
              }
            }
            lines.push('');
          }
        }
      }
    }

    // Async task dump (only for is_self app)
    if (info.async_taskdump) {
      lines.push('--- ASYNC TASK DUMP ---');
      lines.push(`Tokio console enabled: ${info.async_taskdump.tokio_console_enabled}`);
      if (info.async_taskdump.taskdump) {
        lines.push('');
        for (const taskLine of info.async_taskdump.taskdump.split('\n')) {
          lines.push(`  ${taskLine}`);
        }
      }
      lines.push('');
    }

    return lines.join('\n');
  };

  const hasContainers = (detail?.app.container_names.length ?? 0) > 0;

  return (
    <section className="debugging-page" aria-labelledby="debugging-title">
      <header className="debugging-page__header">
        <div>
          <p className="debugging-page__eyebrow">Runtime diagnostics</p>
          <h2 id="debugging-title">Debugging</h2>
          <p className="debugging-page__description">Inspect container logs, then collect system details for deeper troubleshooting.</p>
        </div>
        <button className="secondary" onClick={() => navigate(-1)}>Back</button>
      </header>

      <div className="debugging-page__target">
          <label className="debugging-page__field" htmlFor="debugging-container">
            <span>Container</span>
            <select
              id="debugging-container"
              aria-describedby="debugging-container-help"
              value={container}
              onChange={(e) => setContainer(e.target.value)}
              disabled={!hasContainers}
            >
              {detail?.app.container_names.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <p id="debugging-container-help" className="debugging-page__description">
            Select the container used for both logs and system debug info.
          </p>
          {!hasContainers && <p className="debugging-page__description">No containers configured.</p>}
      </div>

      <div className="debugging-page__panels">
        {/* Docker Logs Section */}
        <section className="debugging-page__panel" aria-labelledby="debugging-logs-title">
          <p className="debugging-page__eyebrow">01 / Inspect</p>
          <h3 id="debugging-logs-title">Container Logs</h3>
          {logsError && <ErrorBanner message={logsError} onDismiss={() => setLogsError(null)} />}
          <div className="debugging-page__controls">
            <label className="debugging-page__field" htmlFor="debugging-lines">
              <span>Log lines</span>
            <input
              id="debugging-lines"
              type="number"
              value={lines}
              onChange={(e) => setLines(Number(e.target.value))}
              min={50}
              max={2000}
            />
            </label>
            <button className="primary" onClick={fetchLogs} disabled={!hasContainers}>
              Load logs
            </button>
          </div>
          <pre className="debugging-page__output" tabIndex={0} role="region" aria-label="Container log output">{output || 'No logs loaded yet.'}</pre>
        </section>

        {/* Debug Info Section */}
        <section className="debugging-page__panel" aria-labelledby="debugging-info-title">
          <p className="debugging-page__eyebrow">02 / Diagnose</p>
          <h3 id="debugging-info-title">System Debug Info</h3>
          {debugInfoError && <ErrorBanner message={debugInfoError} onDismiss={() => setDebugInfoError(null)} />}
          <p className="debugging-page__description">
            Collect diagnostic information for troubleshooting hangs, stalls, or performance issues.
            This may take a minute to complete.
          </p>
          <div className="debugging-page__controls">
            <button className="primary" onClick={fetchDebugInfo} disabled={debugInfoLoading}>
              {debugInfoLoading ?
                (debugProgress ? `Collecting: ${debugProgress.current_step}...` : 'Collecting...') :
                'Collect Debug Info'}
            </button>
            {debugInfo && (
              <>
                <button onClick={copyDebugInfo} className="secondary">
                  {debugInfoCopied ? '✓ Copied JSON' : 'Copy JSON'}
                </button>
                <button onClick={() => {
                  const text = formatDebugInfoAsText(debugInfo);
                  navigator.clipboard.writeText(text);
                }} className="secondary">
                  Copy Text
                </button>
              </>
            )}
          </div>
          <div role="status" aria-live="polite">
          {debugInfoLoading && debugProgress && (
            <div className="debugging-page__progress">
              <p className="status">
                Running: <strong>{debugProgress.current_step}</strong>
                {debugProgress.started_at && (
                  <span className="timing">
                    {' '}(started {new Date(debugProgress.started_at).toLocaleTimeString()})
                  </span>
                )}
              </p>
              {Object.keys(debugProgress.timings).length > 0 && (
                <ul className="timing-list">
                  {Object.entries(debugProgress.timings).map(([step, timing]) => (
                    <li key={step}>
                      {step}: {timing.duration_ms ?? '...'}ms
                      {timing.error && <span className="error"> (error)</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {debugInfoLoading && !debugProgress && (
            <p className="status">Starting debug collection...</p>
          )}
          </div>
          {!debugInfo && !debugInfoLoading && !debugInfoError && (
            <p className="debugging-page__empty">No debug info collected yet. Collect diagnostics to view the report and enable copying.</p>
          )}
          {debugInfo && (
            <pre className="debugging-page__output" tabIndex={0} role="region" aria-label="System debug report">
              {formatDebugInfoAsText(debugInfo)}
            </pre>
          )}
        </section>
      </div>
    </section>
  );
}
